import dayjs from "dayjs"

export const COMPARE_IDS = {
  NONE: "none",
  PREV_PERIOD: "prev_period",
  PREV_WEEK: "prev_week",
  PREV_MONTH: "prev_month",
  PREV_YEAR: "prev_year",
  CUSTOM: "custom"
}

export const COMPARE_BE_VALUES = {
  [COMPARE_IDS.PREV_PERIOD]: "previous_period",
  [COMPARE_IDS.PREV_WEEK]: "previous_week",
  [COMPARE_IDS.PREV_MONTH]: "previous_month",
  [COMPARE_IDS.PREV_YEAR]: "previous_year",
  [COMPARE_IDS.CUSTOM]: "custom"
}

// A relative compare (previous period/week/month/year) is derived server-side from the
// current period, so the backend rejects it with 400 unless both bounds are present. The
// "All time" period sends none, so the param has to be dropped instead of sent bare.
// periodDates is the {start_date, end_date} pair getDates() already builds next to every
// call site; it is required for the relative modes and ignored for the custom one.
export function getCompareParams(compareValue, periodDates) {
  if (!compareValue?.mode) return {}
  const compare = COMPARE_BE_VALUES[compareValue.mode]
  if (!compare) return {}
  if (compareValue.mode === COMPARE_IDS.CUSTOM) {
    const [from, to] = compareValue.range || []
    if (!from || !to) return {}
    return { compare, compare_start_date: from, compare_end_date: to }
  }
  if (!periodDates?.start_date || !periodDates?.end_date) return {}
  return { compare }
}

export const COMPARE_LABELS = {
  [COMPARE_IDS.NONE]: "Do not compare",
  [COMPARE_IDS.PREV_PERIOD]: "Previous period",
  [COMPARE_IDS.PREV_WEEK]: "Previous week",
  [COMPARE_IDS.PREV_MONTH]: "Previous month",
  [COMPARE_IDS.PREV_YEAR]: "Previous year",
  [COMPARE_IDS.CUSTOM]: "Custom period"
}

const ISO = "YYYY-MM-DD"
const toISO = (d) => (d ? d.format(ISO) : null)

const daysBetween = ([from, to]) => (from && to ? dayjs(to).diff(dayjs(from), "day") + 1 : 0)

export function getPeriodDurationDays(currentValue) {
  return daysBetween(Array.isArray(currentValue) ? currentValue : currentValue?.range || [])
}

export function getAvailableCompareIds(currentValue) {
  const duration = getPeriodDurationDays(currentValue)

  // Without both bounds ("All time") there is no range to shift back from, so every
  // relative mode is meaningless here and the backend rejects it. Only an explicit
  // custom range still makes sense.
  if (duration <= 0) return [COMPARE_IDS.CUSTOM, COMPARE_IDS.NONE]

  const ids = [COMPARE_IDS.PREV_PERIOD]
  if (duration <= 7) ids.push(COMPARE_IDS.PREV_WEEK)
  if (duration <= 30) ids.push(COMPARE_IDS.PREV_MONTH)
  if (duration < 365) ids.push(COMPARE_IDS.PREV_YEAR)
  ids.push(COMPARE_IDS.CUSTOM)
  ids.push(COMPARE_IDS.NONE)
  return ids
}

export function isCompareIdAvailable(currentValue, id) {
  if (!id) return false
  return getAvailableCompareIds(currentValue).includes(id)
}

export function getCompareRange(currentValue, compareId) {
  const range = Array.isArray(currentValue) ? currentValue : currentValue?.range || []
  const [fromStr, toStr] = range

  if (!fromStr || !toStr) return [null, null]
  const from = dayjs(fromStr)
  const to = dayjs(toStr)

  switch (compareId) {
    case COMPARE_IDS.PREV_PERIOD: {
      const days = to.diff(from, "day") + 1
      return [toISO(from.subtract(days, "day")), toISO(to.subtract(days, "day"))]
    }
    case COMPARE_IDS.PREV_WEEK:
      return [toISO(from.subtract(7, "day")), toISO(to.subtract(7, "day"))]
    case COMPARE_IDS.PREV_MONTH:
      return [toISO(from.subtract(1, "month")), toISO(to.subtract(1, "month"))]
    case COMPARE_IDS.PREV_YEAR:
      return [toISO(from.subtract(1, "year")), toISO(to.subtract(1, "year"))]
    default:
      return [null, null]
  }
}
